/**
 * Renders a per-brain-type narrative report.
 * Report sections may be plain paragraphs (whitespace-pre-line) or kind:"list"
 * which splits the body on newlines and renders each non-empty line as a bullet.
 */
export default function BrainTypeReport({ report }) {
  if (!report || report.length === 0) return null;

  return (
    <section className="mt-8 space-y-6" data-testid="brain-type-report">
      {report.map((section, idx) => {
        const key = section.heading || `section-${idx}`;
        return (
          <article
            key={key}
            className="bg-white border border-[#E5E2DC] rounded-2xl p-6 sm:p-8"
            data-testid={`brain-type-report-section-${idx}`}
          >
            {section.heading && (
              <h3 className="font-heading text-xl sm:text-2xl text-[#1A1D1A] tracking-tight font-semibold mb-4">
                {section.heading}
              </h3>
            )}

            {section.kind === "list" ? <ListBody body={section.body} /> : <ProseBody body={section.body} />}
          </article>
        );
      })}
    </section>
  );
}

function ProseBody({ body }) {
  return (
    <p className="text-[#1A1D1A] leading-relaxed whitespace-pre-line">{body}</p>
  );
}

function ListBody({ body }) {
  // Split on blank-line groups: intro paragraph(s) before/between bullet groups,
  // and lines without blank-lines = bullet items.
  const blocks = body.split(/\n\s*\n/).map((b) => b.trim()).filter(Boolean);
  return (
    <div className="space-y-4">
      {blocks.map((block, i) => {
        const lines = block.split("\n").map((l) => l.trim()).filter(Boolean);
        // Heuristic: if the block is a single line, treat as intro paragraph;
        // otherwise render as bullet list.
        if (lines.length === 1) {
          return (
            <p key={`p-${lines[0].slice(0, 32)}-${i}`} className="text-[#1A1D1A] leading-relaxed">
              {lines[0]}
            </p>
          );
        }
        return (
          <ul key={`ul-${lines[0].slice(0, 32)}-${i}`} className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 list-disc pl-5 text-[#1A1D1A]">
            {lines.map((item) => (
              <li key={item} className="leading-relaxed">{item}</li>
            ))}
          </ul>
        );
      })}
    </div>
  );
}
